// Lib
import { isNumber, negate } from 'lodash/fp';

// Utils
import { asObject } from '../../../../../common/utils/immutableHelper';
import {
    getAutoColorIndex,
    getCaptionModified,
    getColorSpace,
    getElementId,
} from '../../../../../common/elements/utils/elementPropertyUtils';
import { getElement } from '../../../../../common/elements/utils/elementTraversalUtils';
import { isColorSwatch, isSkeleton } from '../../../../../common/elements/utils/elementTypeUtils';
import { parseColorObject } from '../../../../../common/colors/colorObjectUtil';
import { getColorName } from '../../../../../common/colors/colorNameUtil';
import rawFromText from '../../../../../common/utils/editor/rawUtils/rawFromText';
import { elementAllowsColorCombos } from '../../../../element/board/colorComboPaletteUtils';

// Actions
import { updateMultipleElements } from '../../../../element/actions/elementActions';

// Selectors
import { getElements } from '../../../../element/selectors/elementsSelector';
import { getSelectedElements } from '../../../../element/selection/selectedElementsSelector';
import { getCurrentlyEditingElement } from '../../../../element/selection/currentlyEditingSelector';
import { getSelectedTaskIdSelector } from '../selectionToolsSelector';

// Constants
import { BACKGROUND_COLORS } from '../../../../../common/colors/colorConstants';

export const updateElementsColors =
    ({ color, elements, useExistingColorSpace, secondaryColor }) =>
    (dispatch) => {
        const updates = asObject(
            elements.map((element) => {
                const changes = { color };

                if (elementAllowsColorCombos(element)) {
                    changes.secondaryColor = secondaryColor;
                }

                if (isNumber(getAutoColorIndex(element))) {
                    changes.autoColorIndex = null;
                }

                if (isColorSwatch(element)) {
                    const colorSpace = useExistingColorSpace ? getColorSpace(element) : null;
                    const newColor = parseColorObject(color || '#ffffff', colorSpace);
                    if (newColor) {
                        changes.color = newColor;

                        const isCaptionModified = getCaptionModified(element);
                        if (!isCaptionModified) {
                            changes.caption = rawFromText(getColorName(newColor));
                        }
                    } else {
                        delete changes.color;
                    }
                }

                return {
                    id: getElementId(element),
                    changes,
                };
            }),
        );

        return dispatch(updateMultipleElements({ updates }));
    };

export const updateElementsSecondaryColors =
    ({ color, elements }) =>
    (dispatch) => {
        const updates = asObject(
            elements.map((element) => {
                const changes = { secondaryColor: color };

                return {
                    id: getElementId(element),
                    changes,
                    transactionId: -1,
                };
            }),
        );

        return dispatch(updateMultipleElements({ updates }));
    };

export const updateElementBackgroundColors =
    ({ color, elements }) =>
    (dispatch) => {
        const isTransparent = color === BACKGROUND_COLORS.TRANSPARENT.name;

        const changes = {
            background: isTransparent ? null : { color },
            transparent: { enabled: isTransparent },
        };

        const updates = asObject(
            elements.map((element) => ({
                id: getElementId(element),
                changes,
            })),
        );

        return dispatch(updateMultipleElements({ updates }));
    };

export const updateCurrentlyEditingColor = (color) => (dispatch, getState) => {
    const state = getState();
    const currentlyEditingElement = getCurrentlyEditingElement(state);

    dispatch(updateElementsColors({ color, elements: [currentlyEditingElement] }));
};

export const updateSelectedElementsBackgroundColors = (color) => (dispatch, getState) => {
    const state = getState();
    const elementsToUpdate = getSelectedElements(state).filter(negate(isSkeleton));

    return dispatch(updateElementBackgroundColors({ color, elements: elementsToUpdate }));
};

export const updateSelectedElementsColors =
    ({ color, useExistingColorSpace, secondaryColor }) =>
    (dispatch, getState) => {
        const state = getState();
        const selectedElements = getSelectedElements(state).filter(negate(isSkeleton));

        return dispatch(
            updateElementsColors({ color, elements: selectedElements, useExistingColorSpace, secondaryColor }),
        );
    };

export const updateSelectedElementsSecondaryColors =
    ({ color, useExistingColorSpace }) =>
    (dispatch, getState) => {
        const state = getState();
        const selectedElements = getSelectedElements(state).filter(negate(isSkeleton));

        return dispatch(updateElementsSecondaryColors({ color, elements: selectedElements, useExistingColorSpace }));
    };

export const updateSelectedTaskColor = (color) => (dispatch, getState) => {
    const state = getState();
    const selectedTaskId = getSelectedTaskIdSelector(state);

    const elements = getElements(state);
    const selectedTask = getElement(elements, selectedTaskId);

    return dispatch(updateElementsColors({ color, elements: [selectedTask] }));
};
